import React, { useEffect, useState } from "react";
import api from "../api";
import "../Styles/GeneralLedger.css";

import { useNavigate } from "react-router-dom";

import {
  FileSpreadsheet,
  ArrowLeft,
  Landmark,
  Wallet,
  Scale,
  TrendingUp,
  ChevronRight,
} from "lucide-react";

const GeneralLedger = () => {

  const [ledger, setLedger] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const navigate = useNavigate();

  useEffect(() => {

    const loadLedger = async () => {

      try {

        setLoading(true);

        const accounts =
          await api.getAccounts();

        const fullLedger = [];

        for (const acc of accounts) {

          const txns =
            await api.getTransactions(acc.id);

          const totalDebits =
            txns.reduce(
              (sum, t) =>
                sum +
                parseFloat(
                  t.debit || 0
                ),
              0
            );

          const totalCredits =
            txns.reduce(
              (sum, t) =>
                sum +
                parseFloat(
                  t.credit || 0
                ),
              0
            );

          const balance =
            totalDebits -
            totalCredits;

          fullLedger.push({

            name: acc.name,

            id: acc.id,

            totalDebits,

            totalCredits,

            balance,

          });

        }

        setLedger(fullLedger);

      } catch (error) {

        console.error(
          "❌ Error loading ledger:",
          error
        );

      } finally {

        setLoading(false);

      }

    };

    loadLedger();

  }, []);

  const totalDebits =
    ledger.reduce(
      (sum, entry) =>
        sum + entry.totalDebits,
      0
    );

  const totalCredits =
    ledger.reduce(
      (sum, entry) =>
        sum + entry.totalCredits,
      0
    );

  const overallBalance =
    totalDebits - totalCredits;

  if (loading) {

    return (

      <div className="ledger-page">

        <div className="ledger-loading-card">

          <div className="ledger-spinner"></div>

          <p>
            Loading General Ledger...
          </p>

        </div>

      </div>

    );

  }

  return (

    <div className="ledger-page">

      {/* HERO */}

      <div className="ledger-hero">

        <div className="ledger-hero-left">

          <div className="ledger-pill">

            <FileSpreadsheet size={15} />

            <span>
              General Ledger
            </span>

          </div>

          <h1>
            Ledger
            <br />
            Overview
          </h1>

          <p>
            Review consolidated
            account balances,
            debits, credits and
            financial positioning
            across the accounting
            ecosystem.
          </p>

          <div className="ledger-actions">

            <button
              className="ledger-btn primary"
              onClick={() =>
                navigate("/journals")
              }
            >
              Open Journals
              <ChevronRight size={17} />
            </button>

            <button
              className="ledger-btn secondary"
              onClick={() =>
                navigate("/accounts")
              }
            >
              <ArrowLeft size={16} />
              Accounts
            </button>

          </div>

        </div>

        {/* RIGHT */}

        <div className="ledger-summary-grid">

          <div className="summary-card green">

            <div className="summary-icon">
              <TrendingUp size={28} />
            </div>

            <div>

              <span>Total Debits</span>

              <h2>
                $
                {totalDebits.toLocaleString()}
              </h2>

            </div>

          </div>

          <div className="summary-card red">

            <div className="summary-icon">
              <Scale size={28} />
            </div>

            <div>

              <span>Total Credits</span>

              <h2>
                $
                {totalCredits.toLocaleString()}
              </h2>

            </div>

          </div>

          <div className="summary-card teal">

            <div className="summary-icon">
              <Wallet size={28} />
            </div>

            <div>

              <span>Net Balance</span>

              <h2>
                $
                {Math.abs(
                  overallBalance
                ).toLocaleString()}
              </h2>

            </div>

          </div>

          <div className="summary-card blue">

            <div className="summary-icon">
              <Landmark size={28} />
            </div>

            <div>

              <span>Accounts</span>

              <h2>
                {ledger.length}
              </h2>

            </div>

          </div>

        </div>

      </div>

      {/* TABLE */}

      <div className="ledger-table-wrapper">

        <table className="ledger-table">

          <thead>

            <tr>
              <th>Account</th>
              <th>Debits</th>
              <th>Credits</th>
              <th>Balance</th>
            </tr>

          </thead>

          <tbody>

            {ledger.map((entry) => (

              <tr key={entry.id}>

                <td className="account-name">
                  {entry.name}
                </td>

                <td className="debit-amount">
                  $
                  {entry.totalDebits.toLocaleString()}
                </td>

                <td className="credit-amount">
                  $
                  {entry.totalCredits.toLocaleString()}
                </td>

                <td
                  className={
                    entry.balance >= 0
                      ? "balance-debit"
                      : "balance-credit"
                  }
                >
                  $
                  {Math.abs(
                    entry.balance
                  ).toLocaleString()}

                  <span>
                    (
                    {entry.balance >= 0
                      ? "Dr"
                      : "Cr"}
                    )
                  </span>

                </td>

              </tr>

            ))}

            <tr className="total-row">

              <td>
                Totals
              </td>

              <td>
                $
                {totalDebits.toLocaleString()}
              </td>

              <td>
                $
                {totalCredits.toLocaleString()}
              </td>

              <td>
                $
                {Math.abs(
                  overallBalance
                ).toLocaleString()}

                <span>
                  (
                  {overallBalance >= 0
                    ? "Dr"
                    : "Cr"}
                  )
                </span>

              </td>

            </tr>

          </tbody>

        </table>

      </div>

    </div>

  );

};

export default GeneralLedger;